import { useState, useEffect, useRef } from 'react';
import { bannerService, type BannerWithUrl } from '@/services/banners';
import { imageService } from '@/services/image';

interface BannerCarouselProps {
  onBannerClick?: (banner: BannerWithUrl) => void;
}

export function BannerCarousel({ onBannerClick }: BannerCarouselProps) {
  const [banners, setBanners] = useState<BannerWithUrl[]>([]);
  const [isBannerLoading, setIsBannerLoading] = useState(true);
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  
  // 터치/드래그 관련 상태
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  
  // 자동 슬라이드 타이머 ref
  const autoSlideTimerRef = useRef<NodeJS.Timeout | null>(null);

  // 광고 배너 로드
  useEffect(() => {
    const loadBanners = async () => {
      try {
        setIsBannerLoading(true);
        const result = await bannerService.getActiveBanners();
        
        if (result.success && result.data) {
          
          const bannerUrls = result.data.map(banner => banner.image_url);
          
          // 모든 이미지 프리로드
          await imageService.preloadImages(bannerUrls);
          setBanners(result.data);
        } else {
          console.error('Failed to load banners:', result.error);
          setBanners([]);
        }
      } catch (error) {
        console.error('Failed to load banners:', error);
        setBanners([]);
      } finally {
        setIsBannerLoading(false);
      }
    };

    loadBanners();
  }, []);

  // 자동 슬라이드 기능 (5초마다)
  useEffect(() => {
    if (!isPaused && banners.length > 1 && !isBannerLoading) {
      autoSlideTimerRef.current = setInterval(() => {
        setCurrentBannerIndex((prev) => 
          prev === banners.length - 1 ? 0 : prev + 1
        );
      }, 5000);

      return () => {
        if (autoSlideTimerRef.current) {
          clearInterval(autoSlideTimerRef.current);
        }
      };
    }
  }, [isPaused, banners.length, isBannerLoading, currentBannerIndex]);

  // 배너 변경 함수
  const goToNextBanner = () => {
    setCurrentBannerIndex((prev) => {
      const newIndex = prev === banners.length - 1 ? 0 : prev + 1;
      return newIndex;
    });
  };

  const goToPrevBanner = () => {
    setCurrentBannerIndex((prev) => {
      const newIndex = prev === 0 ? banners.length - 1 : prev - 1;
      return newIndex;
    });
  };

  const goToBanner = (index: number) => {
    setCurrentBannerIndex(index);
  };

  // 터치/드래그 핸들러 (개선됨)
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
    setTouchEnd(null); // null로 초기화
    setIsDragging(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return;
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    
    if (touchStart === null) {
      setIsDragging(false);
      setTouchStart(null);
      setTouchEnd(null);
      return;
    }
    
    // touchEnd가 null이면 탭으로 처리 (드래그 없음)
    if (touchEnd === null) {
      setIsDragging(false);
      setTouchStart(null);
      return;
    }
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 30; // 30px로 민감도 증가
    const isRightSwipe = distance < -30;


    if (isLeftSwipe && banners.length > 1) {
      goToNextBanner();
    }
    if (isRightSwipe && banners.length > 1) {
      goToPrevBanner();
    }
    
    setIsDragging(false);
    setTouchStart(null);
    setTouchEnd(null);
  };

  // 마우스 드래그 핸들러 (개선됨)
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setTouchStart(e.clientX);
    setTouchEnd(null);
    setIsDragging(true);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    e.preventDefault();
    setTouchEnd(e.clientX);
  };

  const handleMouseUp = (e: React.MouseEvent) => {
    e.preventDefault();
    
    if (touchStart === null || !isDragging) {
      setIsDragging(false);
      setTouchStart(null);
      setTouchEnd(null);
      return;
    }
    
    // touchEnd가 null이면 클릭으로 처리
    if (touchEnd === null) {
      setIsDragging(false);
      setTouchStart(null);
      return;
    }
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 30; // 30px로 민감도 증가
    const isRightSwipe = distance < -30;

    if (isLeftSwipe && banners.length > 1) {
      goToNextBanner();
    }
    if (isRightSwipe && banners.length > 1) {
      goToPrevBanner();
    }
    
    setIsDragging(false);
    setTouchStart(null);
    setTouchEnd(null);
  };

  // 배너 클릭 핸들러
  const handleBannerClick = (e: React.MouseEvent) => {
    // 드래그 거리를 체크하여 실제 드래그인지 확인
    if (touchStart !== null && touchEnd !== null) {
      const distance = Math.abs(touchStart - touchEnd);
      if (distance > 5) { // 5px 이상 움직였으면 드래그로 처리
        e.preventDefault();
        return;
      }
    }

    if (banners.length > 0) {
      const currentBanner = banners[currentBannerIndex];
      
      // TODO: URL 이동 기능 (준비됨, 현재 비활성)
      // if (currentBanner.link_url) {
      //   window.open(currentBanner.link_url, '_blank');
      //   return;
      // }
      
      if (onBannerClick) {
        onBannerClick(currentBanner);
      }
    }
  };

  return (
    <aside style={{ marginBottom: '16px' }}>
      <div 
        className="relative overflow-hidden w-full h-[86px] lg:h-[172px] select-none"
        style={{ 
          borderRadius: '14px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          cursor: isDragging ? 'grabbing' : 'grab',
          position: 'relative', // 명시적 position 설정
          minWidth: 0 // flex 아이템일 때 width 계산 문제 해결
        }}
        onClick={handleBannerClick}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={() => {
          setIsDragging(false);
          setTouchStart(null);
          setTouchEnd(null);
          setIsPaused(false);
        }}
        onMouseEnter={() => setIsPaused(true)}
      >
        {isBannerLoading ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="animate-pulse bg-gray-300 w-full h-full rounded-[14px]" />
          </div>
        ) : banners.length > 0 ? (
          <>
            {/* 배너 이미지 컨테이너 - 단순화된 구조 */}
            <div 
              className="flex h-full transition-transform duration-300 ease-in-out"
              style={{ 
                transform: `translateX(-${(currentBannerIndex * 100) / banners.length}%)`,
                width: `${banners.length * 100}%`
              }}
            >
              {banners.map((banner, index) => {
                const itemWidth = 100 / banners.length;
                
                return (
                  <div
                    key={banner.id}
                    className="h-full flex-shrink-0"
                    style={{ 
                      width: `${itemWidth}%`
                    }}
                  >
                    <img 
                      src={banner.image_url}
                      alt={banner.title || `광고 배너 ${index + 1}`}
                      className="w-full h-full lg:object-cover object-contain"
                      style={{ 
                        borderRadius: '14px'
                      }}
                      loading="eager"
                    />
                  </div>
                );
              })}
            </div>

            {/* 인디케이터 */}
            {banners.length > 1 && (
              <div className="absolute bottom-3 left-1/2 transform -translate-x-1/2 flex space-x-2 z-10">
                {banners.map((_, index) => (
                  <button
                    key={index}
                    onClick={(e) => {
                      e.stopPropagation();
                      goToBanner(index);
                    }}
                    className={`transition-all duration-300 ${
                      index === currentBannerIndex 
                        ? 'w-6 h-2 bg-white opacity-90' 
                        : 'w-2 h-2 bg-white opacity-50 hover:opacity-70'
                    }`}
                    style={{ 
                      borderRadius: '4px',
                      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.2)'
                    }}
                    aria-label={`배너 ${index + 1}로 이동`}
                  />
                ))}
              </div>
            )}

            {/* 좌우 화살표 버튼 (데스크톱 전용) */}
            {banners.length > 1 && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    goToPrevBanner();
                  }}
                  className="hidden lg:flex absolute left-2 top-1/2 transform -translate-y-1/2 w-8 h-8 items-center justify-center bg-white bg-opacity-80 rounded-full hover:bg-opacity-100 transition-all z-10"
                  aria-label="이전 배너"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    goToNextBanner();
                  }}
                  className="hidden lg:flex absolute right-2 top-1/2 transform -translate-y-1/2 w-8 h-8 items-center justify-center bg-white bg-opacity-80 rounded-full hover:bg-opacity-100 transition-all z-10"
                  aria-label="다음 배너"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </>
            )}
          </>
        ) : (
          <div className="absolute inset-0 bg-gradient-to-r from-gray-100 to-gray-200 rounded-[14px] flex items-center justify-center">
            <span className="text-gray-400 text-sm">광고 배너</span>
          </div>
        )}
      </div>
    </aside>
  );
}